<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">WireGuard trên Ubuntu</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Hướng dẫn cài đặt WireGuard VPN trên Ubuntu VPS: Thiết lập nhanh và bảo mật (2025)</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>—</span>
          <span class="mx-2">•</span>
          <span>10 phút đọc</span>
          <span class="mx-2">•</span>
          <span>DevOps căn bản</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>WireGuard là gì và tại sao nên sử dụng?</h2>
          <p>WireGuard là giao thức VPN hiện đại, nhanh, bảo mật và dễ cấu hình. Được tích hợp vào Linux kernel từ phiên bản 5.6, WireGuard nhanh hơn OpenVPN 2-3 lần và có code đơn giản hơn.</p>
          
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4 text-blue-800">🚀 Ưu điểm của WireGuard</h3>
            <ul class="space-y-2 text-blue-700">
              <li>• <strong>Hiệu suất cao:</strong> Nhanh hơn OpenVPN 2-3 lần</li>
              <li>• <strong>Bảo mật mạnh:</strong> Sử dụng cryptography hiện đại</li>
              <li>• <strong>Code đơn giản:</strong> Chỉ 4000 dòng code, dễ audit</li>
              <li>• <strong>Cross-platform:</strong> Hỗ trợ Linux, Windows, macOS, iOS, Android</li>
              <li>• <strong>Miễn phí:</strong> Open source, không có phí license</li>
              <li>• <strong>Modern crypto:</strong> ChaCha20, Poly1305, Curve25519</li>
            </ul>
          </div>

          <h2>1. Chuẩn bị môi trường</h2>
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-green-800">📋 Yêu cầu hệ thống</h3>
            <ul class="space-y-2 text-green-700">
              <li>• <strong>OS:</strong> Ubuntu 20.04+ (khuyến nghị 22.04 LTS)</li>
              <li>• <strong>RAM:</strong> Tối thiểu 512MB, khuyến nghị 1GB+</li>
              <li>• <strong>CPU:</strong> 1 core, khuyến nghị 2 cores</li>
              <li>• <strong>Storage:</strong> 10GB trống</li>
              <li>• <strong>Network:</strong> IP public, mở port UDP 51820</li>
              <li>• <strong>Access:</strong> Root hoặc sudo privileges</li>
            </ul>
          </div>

          <h3>Kiểm tra hệ thống:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Kiểm tra OS version
lsb_release -a

# Kiểm tra kernel version (cần 5.6+)
uname -r

# Kiểm tra IP public
curl ifconfig.me

# Kiểm tra firewall
sudo ufw status</code></pre>
          </div>

          <h2>2. Cài đặt WireGuard</h2>
          <p>WireGuard có sẵn trong Ubuntu repository từ phiên bản 20.04. Cài đặt rất đơn giản.</p>

          <h3>Cài đặt từ repository:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Cập nhật package list
sudo apt update

# Cài đặt WireGuard
sudo apt install -y wireguard

# Kiểm tra cài đặt
wg --version</code></pre>
          </div>

          <h2>3. Tạo cặp khóa</h2>
          <p>WireGuard sử dụng public/private key pairs để xác thực. Mỗi peer cần có cặp khóa riêng.</p>

          <h3>Tạo thư mục và khóa:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Tạo thư mục cấu hình
sudo mkdir -p /etc/wireguard
cd /etc/wireguard

# Đặt umask để bảo mật file
umask 077

# Tạo khóa server
sudo wg genkey | sudo tee server_private.key | wg pubkey | sudo tee server_public.key

# Tạo khóa client
sudo wg genkey | sudo tee client_private.key | wg pubkey | sudo tee client_public.key

# Xem khóa public
sudo cat server_public.key
sudo cat client_public.key</code></pre>
          </div>

          <h2>4. Cấu hình Server</h2>
          <p>Tạo file cấu hình cho WireGuard server với các thông số cần thiết.</p>

          <h3>Tạo file cấu hình server:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Tạo file cấu hình
sudo nano /etc/wireguard/wg0.conf

# Nội dung file cấu hình:
[Interface]
# Server private key
PrivateKey = [SERVER_PRIVATE_KEY]

# Server IP trong VPN network
Address = 10.0.0.1/24

# Port lắng nghe
ListenPort = 51820

# DNS server cho clients
DNS = 8.8.8.8, 1.1.1.1

# Firewall rules
PostUp = iptables -A FORWARD -i %i -j ACCEPT; iptables -A FORWARD -o %i -j ACCEPT; iptables -t nat -A POSTROUTING -o eth0 -j MASQUERADE
PostDown = iptables -D FORWARD -i %i -j ACCEPT; iptables -D FORWARD -o %i -j ACCEPT; iptables -t nat -D POSTROUTING -o eth0 -j MASQUERADE

[Peer]
# Client public key
PublicKey = [CLIENT_PUBLIC_KEY]

# Client IP trong VPN network
AllowedIPs = 10.0.0.2/32</code></pre>
          </div>

          <h2>5. Bật IP Forwarding</h2>
          <p>Cần bật IP forwarding để server có thể chuyển tiếp traffic.</p>

          <h3>Cấu hình IP forwarding:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Bật IP forwarding tạm thời
sudo sysctl -w net.ipv4.ip_forward=1

# Bật IP forwarding vĩnh viễn
echo 'net.ipv4.ip_forward = 1' | sudo tee -a /etc/sysctl.conf

# Kiểm tra
sysctl net.ipv4.ip_forward</code></pre>
          </div>

          <h2>6. Cấu hình Firewall</h2>
          <p>Cần mở port UDP 51820 và cấu hình firewall rules.</p>

          <h3>UFW Firewall:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Cài đặt UFW nếu chưa có
sudo apt install -y ufw

# Mở port WireGuard
sudo ufw allow 51820/udp

# Mở SSH (quan trọng!)
sudo ufw allow ssh

# Bật firewall
sudo ufw --force enable

# Kiểm tra status
sudo ufw status</code></pre>
          </div>

          <h2>7. Khởi động WireGuard</h2>
          <p>Khởi động và kiểm tra WireGuard service.</p>

          <h3>Khởi động service:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Khởi động WireGuard
sudo systemctl start wg-quick@wg0

# Bật auto-start
sudo systemctl enable wg-quick@wg0

# Kiểm tra status
sudo systemctl status wg-quick@wg0

# Kiểm tra interface
sudo wg show</code></pre>
          </div>

          <h2>8. Quản lý Clients</h2>
          <p>Thêm/xóa clients và quản lý cấu hình.</p>

          <h3>Thêm client mới:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Tạo khóa cho client mới
sudo wg genkey | sudo tee client2_private.key | wg pubkey | sudo tee client2_public.key

# Thêm peer vào config
sudo wg set wg0 peer [CLIENT2_PUBLIC_KEY] allowed-ips 10.0.0.3/32

# Lưu cấu hình
sudo wg-quick save wg0</code></pre>
          </div>

          <h2>9. Monitoring và Troubleshooting</h2>
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-blue-800">📊 Monitoring Commands</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-blue-600 mb-2">Kiểm tra trạng thái</h4>
                <div class="bg-gray-900 text-green-400 p-3 rounded text-sm">
                  <code># Xem thông tin peers<br/>
                  sudo wg show<br/><br/>
                  # Xem chi tiết<br/>
                  sudo wg show wg0<br/><br/>
                  # Xem logs<br/>
                  sudo journalctl -u wg-quick@wg0</code>
                </div>
              </div>
            </div>
          </div>

          <h2>10. Bảo mật nâng cao</h2>
          <div class="bg-red-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-red-800">🔒 Security Best Practices</h3>
            <ul class="space-y-2 text-red-700">
              <li>• <strong>Backup keys:</strong> Backup khóa private an toàn</li>
              <li>• <strong>Firewall rules:</strong> Chỉ mở port cần thiết</li>
              <li>• <strong>Regular updates:</strong> Cập nhật WireGuard thường xuyên</li>
              <li>• <strong>Key rotation:</strong> Thay đổi khóa định kỳ</li>
              <li>• <strong>Access logs:</strong> Monitor kết nối bất thường</li>
              <li>• <strong>Fail2ban:</strong> Chặn brute force attacks</li>
            </ul>
          </div>

          <div class="bg-green-50 p-6 rounded-lg mt-8">
            <h3 class="font-semibold mb-3 text-green-800">🎉 Kết quả</h3>
            <p class="text-sm text-green-700">Sau khi hoàn thành, bạn sẽ có một WireGuard VPN server hoạt động với hiệu suất cao, bảo mật mạnh và dễ quản lý. WireGuard nhanh hơn OpenVPN 2-3 lần và có cấu hình đơn giản hơn nhiều.</p>
          </div>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'
const title = 'Hướng dẫn cài đặt WireGuard VPN trên Ubuntu VPS: Thiết lập nhanh và bảo mật (2025)'
const desc  = 'Hướng dẫn chi tiết cài đặt và cấu hình WireGuard VPN trên Ubuntu VPS. Thiết lập nhanh, bảo mật cao và hiệu suất tốt hơn OpenVPN'
const url   = 'https://webservices.vn/blog/wireguard-ubuntu-vps-2025'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
